import React, { useState, useEffect, useRef } from 'react';
import { Brain, Power, PowerOff, Clock } from 'lucide-react';
import { apiRequest } from '@/lib/fetchWithAuth';
import { useToast } from '@/hooks/use-toast';
import { ModelSelector } from './ModelSelector';
import {
  getAiReasoningMode,
  setAiReasoningMode,
  type ReasoningMode
} from '@/lib/aiReasoningMode';

type AIModel = 'MISTRAL' | '20B' | '120B';

export const AIControl: React.FC = () => {
  const { toast } = useToast();
  const isRailway = import.meta.env.VITE_RAILWAY_ENVIRONMENT === 'true';
  const [aiStatus, setAiStatus] = useState<'running' | 'stopped'>('stopped');
  const [transition, setTransition] = useState<null | 'starting' | 'stopping'>(null);
  const [autoShutdown, setAutoShutdown] = useState(true); // Always active for cost savings
  const shutdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [shortPoll, setShortPoll] = useState<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const [currentModel, setCurrentModel] = useState<AIModel>(isRailway ? '120B' : 'MISTRAL');
  const [availableModels, setAvailableModels] = useState<AIModel[]>(isRailway ? ['120B'] : ['MISTRAL', '120B', '20B']);
  const [blockStatusCheck, setBlockStatusCheck] = useState(false);
  const [reasoningMode, setReasoningMode] = useState<ReasoningMode>(() => getAiReasoningMode());

  const normalizeModel = (rawModel: unknown, rawType?: unknown): AIModel => {
    const type = typeof rawType === 'string' ? rawType.trim().toLowerCase() : '';
    if (type === 'local') {
      return 'MISTRAL';
    }

    const value = typeof rawModel === 'string' ? rawModel.trim().toUpperCase() : '';
    if (value === 'MISTRAL' || value.includes('QWEN') || value.includes('OLLAMA')) {
      return 'MISTRAL';
    }
    if (value.includes('20B')) {
      return '20B';
    }
    if (value.includes('120B')) {
      return '120B';
    }

    return isRailway ? '120B' : 'MISTRAL';
  };

  const normalizeAvailableModels = (rawModels: unknown, current: AIModel): AIModel[] => {
    if (!Array.isArray(rawModels) || rawModels.length === 0) {
      const fallback = isRailway
        ? [current, '120B', '20B']
        : [current, 'MISTRAL', '120B', '20B'];
      return fallback.filter((m, i, arr) => arr.indexOf(m) === i) as AIModel[];
    }

    const mapped = rawModels
      .map((m) => normalizeModel(m))
      .filter((m, i, arr) => arr.indexOf(m) === i) as AIModel[];

    if (!mapped.includes(current)) {
      mapped.unshift(current);
    }

    return mapped;
  };

  const isLocalModel = normalizeModel(currentModel) === 'MISTRAL';
  const modelDisplayName = (model: unknown) => normalizeModel(model) === 'MISTRAL' ? 'Qwen3 14B (Local)' : `GPT-OSS-${normalizeModel(model)}`;

  const reasoningModeLabel = (mode: ReasoningMode) => {
    if (mode === 'FAST') return 'Fast';
    if (mode === 'REASONING') return 'Reasoning';
    return 'Normal';
  };

  const handleReasoningModeChange = (mode: ReasoningMode) => {
    const normalized = mode;
    setReasoningMode(normalized);
    setAiReasoningMode(normalized);
    toast({
      title: `AI Mode: ${reasoningModeLabel(normalized)}`,
      description: normalized === 'FAST'
        ? 'Lowest latency, shortest answers.'
        : normalized === 'REASONING'
          ? 'Deeper analysis, longer/slower answers.'
          : 'Balanced speed and quality.',
      duration: 2200
    });
  };

  // Check AI status and current model on mount
  useEffect(() => {
    checkAIStatus();
    loadCurrentModel();
    const interval = setInterval(checkAIStatus, 30000); // Check every 30 seconds
    return () => {
      clearInterval(interval);
      if (shortPoll) clearInterval(shortPoll);
    };
  }, []);

  const loadCurrentModel = async () => {
    try {
      const response = await apiRequest('/api/ai/current-model');
      const normalizedCurrent = normalizeModel(response.current_model, response.type);
      setCurrentModel(normalizedCurrent);
      setAvailableModels(normalizeAvailableModels(response.available_models, normalizedCurrent));
    } catch (error) {
      console.error('Failed to load current model:', error);
      // Fallback defaults if backend is unreachable
      setCurrentModel(isRailway ? '120B' : 'MISTRAL');
    }
  };

  // Auto-shutdown timer (RunPod models only)
  useEffect(() => {
    if (!isLocalModel && aiStatus === 'running' && transition === null) {
      // Only start countdown when AI is actually ready (green brain)
      setTimeRemaining(300); // 5 minutes in seconds
      setAutoShutdown(true); // Always enable auto-shutdown when AI is ready
      
      const countdown = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            stopAI();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      countdownRef.current = countdown;

      const timer = setTimeout(() => {
        stopAI();
        clearInterval(countdown);
        countdownRef.current = null;
      }, 300000); // 5 minutes

      shutdownTimerRef.current = timer;
      
      return () => {
        clearTimeout(timer);
        clearInterval(countdown);
        countdownRef.current = null;
        shutdownTimerRef.current = null;
      };
    } else if (shutdownTimerRef.current) {
      clearTimeout(shutdownTimerRef.current);
      shutdownTimerRef.current = null;
      setTimeRemaining(0);
      setAutoShutdown(false);
    }
  }, [aiStatus, transition, isLocalModel]); // Only start countdown when transition clears (model ready)

  // Reset auto-shutdown timer on AI activity (RunPod only)
  useEffect(() => {
    const onActivity = () => {
      if (!isLocalModel && aiStatus === 'running' && transition === null) {
        // Only reset timer when AI is actually ready (not loading)
        setTimeRemaining(300); // Reset to 5 minutes
        if (shutdownTimerRef.current) clearTimeout(shutdownTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        const countdown = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev <= 1) {
              stopAI();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        countdownRef.current = countdown;

        const timer = setTimeout(() => {
          stopAI();
          clearInterval(countdown);
          countdownRef.current = null;
        }, 300000); // 5 minutes
        shutdownTimerRef.current = timer;
      }
    };
    window.addEventListener('ai-activity', onActivity);
    return () => window.removeEventListener('ai-activity', onActivity);
  }, [aiStatus, isLocalModel, transition]);

  const checkAIStatus = async () => {
    // Skip status check if blocked (during critical operations)
    if (blockStatusCheck || transition === 'stopping') {
      return;
    }

    try {
      const response = await apiRequest(`/api/ai/status?ts=${Date.now()}`);
      const serverStatus: 'running' | 'stopped' | 'loading' = response.status;
      const modelReady: boolean | undefined = response.modelReady;
      const autoStopEnabled: boolean = response.autoStopEnabled !== false;
      const remainingMs: number | null = typeof response.msRemaining === 'number' ? response.msRemaining : null;
      if (isLocalModel || !autoStopEnabled) {
        setTimeRemaining(0);
      } else if (remainingMs !== null) {
        setTimeRemaining(Math.max(0, Math.ceil(remainingMs / 1000)));
      } else {
        setTimeRemaining(0);
      }

      // Don't change transition state if we're currently starting - prevents race condition
      if (transition === 'starting') {
        return;
      }

      // Maintain yellow state while warming up (pod started but model endpoint not ready)
      if ((serverStatus === 'running' || serverStatus === 'loading') && modelReady === false) {
        if (transition !== 'starting') setTransition('starting');
        setAiStatus('stopped');
      } else if (serverStatus === 'running' && modelReady === true) {
        setAiStatus('running');
        setTransition(null);
        if (shortPoll) {
          clearInterval(shortPoll);
          setShortPoll(null);
        }
      } else {
        setAiStatus('stopped');
        setTransition(null);
        if (shortPoll) {
          clearInterval(shortPoll);
          setShortPoll(null);
        }
      }
    } catch (error) {
      console.error('Failed to check AI status:', error);
    }
  };

  const beginShortPolling = () => {
    if (shortPoll) clearInterval(shortPoll);
    const id = setInterval(checkAIStatus, 1000);
    setShortPoll(id);
    // Safety stop after 2 minutes - use the id directly to avoid closure issues
    setTimeout(() => {
      clearInterval(id);
      setShortPoll(null);
    }, 120000);
  };

  const startAI = async () => {
    if (isLocalModel) {
      setAiStatus('running');
      setTransition(null);
      setTimeRemaining(0);
      toast({
        title: "Qwen Is Always On",
        description: "Local Qwen3 14B does not require start/stop or auto-shutdown.",
      });
      return;
    }

    setTransition('starting');
    try {
      await apiRequest('/api/ai/start', { method: 'POST' });
      toast({
        title: "AI Starting",
        description: `${modelDisplayName(currentModel)} is starting up. This will take 3-5 minutes.`,
      });
      beginShortPolling();
    } catch (error: any) {
      console.error('Failed to start AI:', error);
      const errorMessage = error.details || error.error || "Failed to start AI pod";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setAiStatus('stopped');
      setTransition(null);
      if (shortPoll) {
        clearInterval(shortPoll);
        setShortPoll(null);
      }
    }
  };

  const stopAI = async () => {
    if (isLocalModel) {
      setTransition(null);
      setAiStatus('running');
      setTimeRemaining(0);
      if (shutdownTimerRef.current) {
        clearTimeout(shutdownTimerRef.current);
        shutdownTimerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      toast({
        title: "Qwen Stays On",
        description: "Local Qwen3 14B has no auto-stop timer.",
      });
      return;
    }

    setTransition('stopping');
    setBlockStatusCheck(true); // Block status checks during stop

    try {
      await apiRequest('/api/ai/stop', { method: 'POST' });
      toast({
        title: "AI Stopped",
        description: `${modelDisplayName(currentModel)} has been stopped to save costs.`,
      });
      setAiStatus('stopped');
      setTransition(null);
      if (shortPoll) {
        clearInterval(shortPoll);
        setShortPoll(null);
      }

      // Wait a moment before re-enabling status checks
      setTimeout(() => {
        setBlockStatusCheck(false);
      }, 3000);

    } catch (error: any) {
      console.error('Failed to stop AI:', error);
      const errorMessage = error.details || error.error || "Failed to stop AI pod";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Reset auto-shutdown if stop failed
      if (autoShutdown) {
        setAutoShutdown(false);
        setTimeRemaining(0);
        if (shutdownTimerRef.current) {
          clearTimeout(shutdownTimerRef.current);
          shutdownTimerRef.current = null;
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        toast({
          title: "Auto-Shutdown Cancelled",
          description: "Auto-shutdown cancelled due to stop failure. Please try stopping manually.",
          variant: "destructive",
        });
      }

      setTransition(null);
      setBlockStatusCheck(false); // Re-enable status checks even on failure
      // Don't start short polling on stop failure to prevent infinite loops
      // The user can manually refresh status or try stopping again
    }
  };

  const toggleAutoShutdown = () => {
    if (isLocalModel) {
      toast({
        title: "No Timer For Local Qwen",
        description: "Auto-shutdown applies only to RunPod models (20B/120B).",
        variant: "default",
      });
      return;
    }

    // Auto-shutdown is now always active - this function is disabled
    toast({
      title: "Auto-Shutdown Always Active",
      description: "5-minute auto-shutdown is permanently enabled to save costs during development",
      variant: "default",
    });
  };

  const handleModelChange = async (newModel: AIModel) => {
    try {
      setTransition('stopping');

      // If switching to MISTRAL (local model)
      if (newModel === 'MISTRAL') {
        // Stop RunPod if it's running
        if (aiStatus === 'running') {
          toast({
            title: "Switching to Qwen3",
            description: "Stopping RunPod and switching to local Qwen3 14B (free)...",
          });
          await apiRequest('/api/ai/stop', { method: 'POST' });
        }

        // Persist backend model switch to ensure chat/status routes use MISTRAL.
        await apiRequest('/api/ai/switch-model', {
          method: 'POST',
          data: {
            target_model: 'MISTRAL',
            organization_id: undefined
          }
        });

        setCurrentModel('MISTRAL');
        setAiStatus('running');
        setTimeRemaining(0);
        if (shutdownTimerRef.current) {
          clearTimeout(shutdownTimerRef.current);
          shutdownTimerRef.current = null;
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        setTransition(null);

        toast({
          title: "Switched to Qwen3",
          description: "Now using local Qwen3 14B (always-on, free). Use AI chat immediately!",
        });
        return;
      }

      // If switching from MISTRAL to RunPod (20B/120B)
      if (currentModel === 'MISTRAL') {
        toast({
          title: "Switching to RunPod",
          description: `Switching to ${newModel} model. Click Start AI to begin.`,
        });

        setCurrentModel(newModel);
        setAiStatus('stopped');
        setTransition(null);

        // Update backend configuration
        await apiRequest('/api/ai/switch-model', {
          method: 'POST',
          data: {
            target_model: newModel,
            organization_id: undefined
          }
        });

        return;
      }

      // Switching between RunPod models (20B ↔ 120B)
      if (aiStatus === 'running') {
        toast({
          title: "Switching Models",
          description: `Stopping current model and switching to ${newModel}...`,
        });

        await apiRequest('/api/ai/stop', { method: 'POST' });
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      await apiRequest('/api/ai/switch-model', {
        method: 'POST',
        data: {
          target_model: newModel,
          organization_id: undefined
        }
      });

      setCurrentModel(newModel);
      setTransition(null);

      toast({
        title: "Model Switched",
        description: `Now using ${newModel} model. Click Start AI to begin.`,
      });

    } catch (error: any) {
      setTransition(null);
      toast({
        title: "Model Switch Failed",
        description: error.message || "Failed to switch AI model",
        variant: "destructive",
      });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-1 px-2 overflow-visible">
      {/* Status Indicator with Model Selector */}
      <div className="flex items-center gap-3 mr-2 relative">
        <Brain className={`h-6 w-6 ${
          transition
            ? 'text-yellow-500 animate-pulse'
            : aiStatus === 'running'
              ? 'text-green-500'
              : 'text-red-500'
        }`} />
        
        {/* Model Selector Dropdown */}
        <ModelSelector
          currentModel={currentModel}
          onModelChange={handleModelChange}
          availableModels={availableModels}
          disabled={transition !== null}
          aiStatus={aiStatus}
        />

        {/* Fast / Normal / Reasoning mode selector */}
        <div
          className="flex items-center rounded-md border border-gray-300 bg-white overflow-hidden"
          title="AI response mode"
        >
          {(['FAST', 'NORMAL', 'REASONING'] as ReasoningMode[]).map((mode) => {
            const active = reasoningMode === mode;
            return (
              <button
                key={mode}
                onClick={() => handleReasoningModeChange(mode)}
                className={`px-2 py-1 text-xs font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {reasoningModeLabel(mode)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Start AI Button - Colors match brain state */}
      <button
        onClick={startAI}
        disabled={isLocalModel || aiStatus !== 'stopped' || transition === 'starting'}
        className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
          aiStatus === 'running' && transition === null
            ? 'bg-green-500 text-white cursor-not-allowed'
            : transition === 'starting'
              ? 'bg-yellow-400 text-black cursor-wait'
              : transition === 'stopping'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-green-100 hover:text-green-700'
        }`}
      >
        <Power className="h-3 w-3" />
        Start
      </button>

      {/* Stop AI Button - Colors match brain state */}
      <button
        onClick={stopAI}
        disabled={isLocalModel || aiStatus !== 'running' || transition === 'stopping'}
        className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
          aiStatus === 'stopped'
            ? 'bg-red-500 text-white cursor-not-allowed'
            : transition === 'stopping'
              ? 'bg-yellow-400 text-black cursor-wait'
              : transition === 'starting'
                ? 'bg-yellow-400 text-black cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-red-100 hover:text-red-700'
        }`}
      >
        <PowerOff className="h-3 w-3" />
        Stop
      </button>

      {/* Auto Shutdown Button */}
      {!isLocalModel && (
        <button
          onClick={toggleAutoShutdown}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
            aiStatus === 'running'
              ? timeRemaining > 0
                ? 'bg-blue-500 text-white'
                : 'bg-purple-500 text-white'
              : 'bg-purple-500 text-white'
          }`}
          title="5-minute auto-shutdown is always active to save costs"
        >
          <Clock className="h-3 w-3" />
          {timeRemaining > 0 ? formatTime(timeRemaining) : '5min'}
        </button>
      )}

      {/* Model Indicator */}
      <div className="ml-2 text-xs text-gray-500">
        {aiStatus === 'running' && !transition && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
            AI Active
          </span>
        )}
        {aiStatus === 'stopped' && !transition && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 bg-red-500 rounded-full"></span>
            AI Offline
          </span>
        )}
        {transition && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></span>
            {transition === 'starting' ? 'Starting...' : 'Stopping...'}
          </span>
        )}
      </div>
    </div>
  );
};
